/**
 * Which shared mark a long-tail connector draws, resolved the same way on both
 * clients.
 *
 * The admin-registered long tail (every MCP server, every OpenAPI spec) has no
 * `provider`, so the provider maps cannot reach a mark for it. It does have a
 * catalog slug and a homepage, and `shared/assets/connectors` is keyed by brand
 * name — so a slug matches directly when the admin named the connector after
 * the brand, and failing that the homepage's brand label does:
 * `mcp.airtable.com` and `airtable.com` both read as `airtable`, so a mark
 * follows a service across the several hosts it may be registered under.
 *
 * Both clients call this one resolver over their own generated map, which is
 * what keeps a catalog connector drawing the same mark on web and on the phone.
 */

/**
 * Catalog slugs that name a mark under a different file. The admin's slug is
 * free text; the file name is whatever the Figma library called the mark.
 */
const MARK_ALIASES: Readonly<Record<string, string>> = {
  "google-sheets": "google-sheet",
};

/**
 * Marks the provider maps already own. The library tier never draws these: a
 * long-tail service hosted under `*.google.com` keeps its own favicon rather
 * than wearing Google's "G", and a slug of `device` is not the phone glyph.
 */
const PROVIDER_MARKS: ReadonlySet<string> = new Set([
  "device",
  "gmail",
  "google",
  "google-calendar",
  "google-drive",
  "microsoft-teams",
  "outlook",
  "slack",
  "text-messages",
  "whatsapp",
  "zoom",
]);

/** `https://mcp.airtable.com` → `airtable`; `https://affinity.co` → `affinity`. Undefined when unparsable. */
const brandLabelOfHost = (websiteUrl: string): string | undefined => {
  try {
    const labels = new URL(websiteUrl).hostname.toLowerCase().split(".");
    return labels.length >= 2 ? labels[labels.length - 2] : labels[0];
  } catch {
    return undefined;
  }
};

/**
 * `marks[key]` for a key the map itself holds. A homepage like
 * `constructor.io` must not reach `Object.prototype.constructor` and hand back
 * a function that is not a mark.
 */
const libraryMark = <Mark>(
  marks: Readonly<Record<string, Mark>>,
  key: string | undefined,
): Mark | undefined =>
  key && !PROVIDER_MARKS.has(key) && Object.hasOwn(marks, key) ? marks[key] : undefined;

/**
 * The first mark in `marks` a long-tail connector can be drawn with: by its
 * catalog slug (or that slug's alias), then by its homepage's brand label.
 * Undefined when we ship none, so the caller falls back to the service's
 * favicon.
 *
 * Generic over the mark so each client passes its own generated map — web's
 * holds DOM components, native's react-native-svg ones — and the lookup rule
 * lives in one place.
 */
export const connectorMarkFor = <Mark>(
  marks: Readonly<Record<string, Mark>>,
  { slug, websiteUrl }: { slug?: string | null; websiteUrl?: string | null },
): Mark | undefined => {
  if (slug) {
    const bySlug = libraryMark(marks, slug) ?? libraryMark(marks, MARK_ALIASES[slug]);
    if (bySlug) return bySlug;
  }
  return websiteUrl ? libraryMark(marks, brandLabelOfHost(websiteUrl)) : undefined;
};
